import { useState } from 'react'
import { Bot, Plus, Check, Lightbulb } from 'lucide-react'
import type { Promptbouwer } from '../lib/types'

/* ===========================================================================
 *  De vraag opbouwen
 *
 *  Uitleggen dat een betere vraag een beter antwoord geeft, overtuigt
 *  niemand. Het naast elkaar zien wel: links de kale vraag met het algemene
 *  antwoord, en na vier tikken hetzelfde ding dat ineens iets bruikbaars
 *  teruggeeft.
 *
 *  Er zit één knop tussen die NIET helpt -- "wees beleefd". Die staat er
 *  omdat de meest voorkomende misvatting is dat langer beter is. Je kunt hem
 *  aanzetten, hij komt doorgestreept in de vraag te staan, en de meter gaat
 *  er niet van omhoog. Dat is een les die je met een zin niet overbrengt.
 *
 *  Het antwoord onderaan is VOORGESCHREVEN en komt niet van een echte
 *  chatbot. Dat moet ook niet: dan gaat deze cursus over de lijn naar een
 *  leverancier, en dat is precies wat module 3 afraadt.
 * =========================================================================== */

export default function Bouwer({
  blok, opKlaar,
}: {
  blok: Promptbouwer
  opKlaar: () => void
}) {
  const [aan, setAan] = useState<Set<string>>(new Set())
  const [laatst, setLaatst] = useState<string | null>(null)

  const nodig = blok.stukken.filter((s) => s.nodig)
  const gekozenNodig = nodig.filter((s) => aan.has(s.id)).length
  const vol = gekozenNodig === nodig.length
  const deel = Math.round((gekozenNodig / nodig.length) * 100)

  function wissel(id: string) {
    setAan((was) => {
      const nu = new Set(was)
      if (nu.has(id)) { nu.delete(id); setLaatst(null) } else { nu.add(id); setLaatst(id) }

      /* Zodra alle nodige stukken erin zitten, is de oefening gedaan. Ook als
         iemand daarna nog wat aan- en uitzet. */
      const klaar = blok.stukken.filter((s) => s.nodig).every((s) => nu.has(s.id))
      if (klaar) opKlaar()
      return nu
    })
  }

  const gekozen = blok.stukken.filter((s) => aan.has(s.id))
  const uitleg = laatst ? blok.stukken.find((s) => s.id === laatst) : null

  return (
    <div className="bouwer">
      <p className="opdracht">{blok.opdracht}</p>

      <div className="chips">
        {blok.stukken.map((stuk) => (
          <button
            key={stuk.id}
            type="button"
            className={`chip ${aan.has(stuk.id) ? 'aan' : ''} ${aan.has(stuk.id) && !stuk.nodig ? 'nutteloos' : ''}`}
            onClick={() => wissel(stuk.id)}
          >
            {aan.has(stuk.id) ? <Check size={14} /> : <Plus size={14} />}
            {stuk.label}
          </button>
        ))}
      </div>

      <div className="promptvak">
        {gekozen.length === 0 ? (
          <span className="leeg">Tik hierboven een onderdeel aan…</span>
        ) : (
          <>
            {gekozen.map((stuk, i) => (
              <span key={stuk.id} className={`deel ${stuk.nodig ? '' : 'nutteloos'}`}>
                {i > 0 ? ', ' : ''}
                {i === 0 ? hoofdletter(stuk.tekst) : stuk.tekst}
              </span>
            ))}
            <span className="deel">.</span>
          </>
        )}
      </div>

      <div className="meter">
        <div className="baan" aria-hidden>
          {/* De breedte staat hier gewoon op het element; de overgang ernaartoe
              zit in de stijl van .baan > i. */}
          <i
            style={{
              width: `${deel}%`,
              background: vol
                ? 'linear-gradient(90deg, var(--ok), #7ee2ad)'
                : 'linear-gradient(90deg, var(--brand), #ffd97a)',
            }}
          />
        </div>
        <span className="waarde">{gekozenNodig} van {nodig.length} nuttig</span>
      </div>

      {uitleg && (
        <div key={uitleg.id} className="waarom opkomst">
            <Lightbulb
              size={15}
              style={{ verticalAlign: -2, marginRight: 8, color: 'var(--text-brand)' }}
            />
          {uitleg.waarom}
        </div>
      )}

      <div className="antwoordvak">
        <div className="kop">
          <Bot size={15} />
          <span>Wat je dan terugkrijgt</span>
        </div>
        <div key={vol ? 'vol' : 'kaal'} className="lijf opkomst">
          {vol ? blok.volAntwoord : blok.kaalAntwoord}
        </div>
      </div>
    </div>
  )
}

function hoofdletter(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1)
}
